import { Text, Tooltip, Divider, Image, Center, Stack } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { PersonName } from "@components/PersonName"
import { FilePreview } from "@components/FilePreview"
import { getFormattedCurrency } from "@/utils/get-formatted-number"
import { getFormattedDate } from "@/utils/get-formatted-date"
import { DetailsCard } from "@/routes/app/-components/details-card"
import { ColumnLayout } from "@/routes/app/-components/column-layout"
import {
  EditResourceObjectDetailsActionIcon,
  BorrowResourceObjectActionIcon,
  EditResourceObjectFieldsActionIcon,
} from "./resource-object-action-icons"
import { LabeledValue } from "@components/default-list/labeled-value"
import { FieldData } from "@components/field-data"

interface StartTabProps {
  condition: string | null | undefined
  resourceObject: any
  resource: any
  resourceObjectId: number
  bestEstimate: any
  activeBorrow: any
  activePrivateInsurances: any[]
  activeBusinessInsurances: any[]
  thumbnailFailed: boolean
  setThumbnailFailed: (failed: boolean) => void
  onEditResourceDetailsClick: () => void
  onLendClick: () => void
  onReturnClick: () => void
  onEditObjectFieldsClick: () => void
  attachmentApi: any
}

export function StartTab({
  condition,
  resourceObject,
  resource,
  resourceObjectId,
  bestEstimate,
  activeBorrow,
  activePrivateInsurances,
  activeBusinessInsurances,
  thumbnailFailed,
  setThumbnailFailed,
  onEditResourceDetailsClick,
  onLendClick,
  onReturnClick,
  onEditObjectFieldsClick,
  attachmentApi,
}: StartTabProps) {
  const { t } = useTranslation("app_server")

  const detailsData = [
    { label: t("Name"), value: resourceObject.name },
    {
      label: t("Owner"),
      value: resourceObject.personId ? (
        <PersonName personId={resourceObject.personId} />
      ) : undefined,
    },
    {
      label: t("PurchasePrice"),
      value: resourceObject.purchasePrice
        ? getFormattedCurrency(resourceObject.purchasePrice)
        : undefined,
    },
    {
      label: t("Category"),
      value: resource
        ? t(`ResourceTypeCategory_${resource.category}` as any)
        : undefined,
    },
    { label: t("InventoryNumber"), value: resourceObject.identificationCode },
    {
      label: t("Condition"),
      value: condition ?? undefined,
    },
    {
      label: t("Available"),
      value: resourceObject.available ? t("Yes") : t("No"),
    },
    { label: t("Type"), value: resource?.resourceType },
    {
      label: t("Value"),
      value:
        (bestEstimate?.monetaryValue ?? bestEstimate?.valuation)
          ? getFormattedCurrency(
              bestEstimate?.monetaryValue ?? bestEstimate?.valuation
            )
          : undefined,
    },
    { label: t("Location"), value: resource?.location },
    {
      label: t("RentalFee"),
      value: resource?.rentalFee
        ? getFormattedCurrency(resource.rentalFee)
        : undefined,
    },
    {
      label: t("CleaningCost"),
      value: resource?.cleaningCost
        ? getFormattedCurrency(resource.cleaningCost)
        : undefined,
    },
  ]

  return (
    <ColumnLayout>
      <DetailsCard
        title={
          resourceObject?.name ?? `${t("ResourceObject")} ${resourceObjectId}`
        }
        buttons={
          <EditResourceObjectDetailsActionIcon
            onClick={onEditResourceDetailsClick}
          />
        }
      >
        {detailsData
          .filter(
            (d) =>
              !!d.value && d.label !== t("Name") && d.label !== t("Available")
          )
          .map((data, i) => (
            <LabeledValue key={i} {...data} />
          ))}
      </DetailsCard>

      <DetailsCard
        title={t("Status")}
        buttons={
          <BorrowResourceObjectActionIcon
            onClick={activeBorrow ? onReturnClick : onLendClick}
            isBorrowed={!!activeBorrow}
          />
        }
      >
        {activeBorrow ? (
          <>
            <LabeledValue
              label={t("LentTo")}
              value={
                activeBorrow.personId ? (
                  <PersonName personId={activeBorrow.personId} />
                ) : activeBorrow.location ? (
                  activeBorrow.location.name
                ) : (
                  t("Unknown")
                )
              }
            />
            {activeBorrow.resourceObjectBorrowSet && (
              <LabeledValue
                label={t("PartOfDeliveryNote")}
                value={`${activeBorrow.resourceObjectBorrowSet.setNumber}`}
              />
            )}
            {activeBorrow.endDate && (
              <LabeledValue
                label={t("ExpectedReturnDate")}
                value={getFormattedDate(activeBorrow.endDate)}
              />
            )}
            {activeBorrow.expectedEndDate && (
              <LabeledValue
                label={t("ExpectedReturnDate")}
                value={getFormattedDate(activeBorrow.expectedEndDate)}
              />
            )}
            {activeBorrow.note && (
              <LabeledValue label={t("Note")} value={activeBorrow.note} />
            )}
          </>
        ) : (
          <Text color="dimmed">{t("Available")}</Text>
        )}

        {(activePrivateInsurances.length > 0 ||
          activeBusinessInsurances.length > 0) && (
          <Stack>
            {activePrivateInsurances.length > 0 && (
              <Stack>
                <Divider my="sm" />
                <Text color="dimmed">{t("PrivateInsured")}</Text>
                {activePrivateInsurances.map((insurance) => (
                  <Stack
                    key={insurance.resourceObjectInsuranceId}
                    gap="xs"
                    ml="md"
                  >
                    {insurance.insuredPersonId && (
                      <Stack>
                        <Divider />
                        <LabeledValue
                          label={t("Owner")}
                          value={
                            <PersonName personId={insurance.insuredPersonId} />
                          }
                        />
                      </Stack>
                    )}
                    {insurance.endDate && (
                      <Stack>
                        <Divider />
                        <LabeledValue
                          label={t("ValidTo")}
                          value={getFormattedDate(insurance.endDate)}
                        />
                      </Stack>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
            {activeBusinessInsurances.length > 0 && (
              <Stack>
                <Divider my="sm" />
                <Text color="dimmed">{t("BusinessInsured")}</Text>
                {activeBusinessInsurances.map((insurance) => (
                  <Stack
                    key={insurance.resourceObjectInsuranceId}
                    gap="xs"
                    ml="md"
                  >
                    <Divider />
                    {insurance.insuredPersonId && (
                      <LabeledValue
                        label={t("Person")}
                        value={
                          <PersonName personId={insurance.insuredPersonId} />
                        }
                      />
                    )}
                    {insurance.endDate && (
                      <LabeledValue
                        label={t("ValidTo")}
                        value={getFormattedDate(insurance.endDate)}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </DetailsCard>

      {resourceObject.attachments &&
        resourceObject.attachments.length > 0 &&
        (() => {
          const a = resourceObject.attachments[0]
          const attachmentId = a?.attachmentId
          const fileName = a?.fileName

          if (!attachmentId) return null

          const thumbnail = attachmentApi.getThumbnailUrl(
            attachmentId,
            resourceObject.resourceObjectId,
            800,
            true
          )
          const fileUrl = attachmentApi.getFileUrl(
            attachmentId,
            resourceObject.resourceObjectId
          )

          return (
            <DetailsCard title={t("Attachment")}>
              {!thumbnailFailed ? (
                <Center style={{ width: "100%", height: "auto" }}>
                  <Image
                    src={thumbnail}
                    alt={fileName}
                    fit="contain"
                    maw="100%"
                    h="auto"
                    onError={() => setThumbnailFailed(true)}
                  />
                </Center>
              ) : (
                <FilePreview
                  h={"100%"}
                  src={fileUrl}
                  fileName={fileName ?? ""}
                />
              )}
            </DetailsCard>
          )
        })()}

      {resourceObject.objectFields &&
        resourceObject.objectFields.length > 0 && (
          <DetailsCard
            title={t("ObjectProperties")}
            buttons={
              <EditResourceObjectFieldsActionIcon
                onClick={onEditObjectFieldsClick}
              />
            }
          >
            {resourceObject.objectFields.map((field: any, i: number) => (
              <LabeledValue
                key={i}
                label={field.type?.name ?? t("Field")}
                value={field.textValue ?? ""}
              />
            ))}
          </DetailsCard>
        )}

      {resourceObject.resourceFields &&
        resourceObject.resourceFields.length > 0 && (
          <DetailsCard title={t("ResourceFields")}>
            {resourceObject.resourceFields.map((rf: any, i: number) => (
              <LabeledValue
                key={i}
                label={rf.type.name}
                value={<FieldData fieldData={rf} />}
              />
            ))}
          </DetailsCard>
        )}
    </ColumnLayout>
  )
}
